import { useCallback, useEffect, useImperativeHandle, useReducer, useRef } from 'react'

const GLYPHS = [
  '⎎', '⎍', '⌰', '⌇', '⏁', '⏃', '☊', '☍',
  '⎅', '⟒', '⎐', '⍜', '⌿', '⍀', '⟊', '⋏',
  '⊑', '⟟', '⋔', '⍙', '⏚', '⊬', '☌', '⌱',
  '⌀', '⌁', '⌂', '⌃', '⌄', '⌅', '⌆', '⌇',
  '⌈', '⌉', '⌊', '⌋', '⌌', '⌍', '⌎', '⌏',
  '⌐', '⌑', '⌒', '⌓', '⌔', '⌕', '⌖', '⌗',
  '⌘', '⌙', '⌬', '⌭', '⌮', '⌯', '⌱', '⌲',
  '⌳', '⌴', '⌵', '⌶', '⌷', '⌸', '⌹', '⌺',
  '⌻', '⌼', '⌽', '⌾',
  '⍁', '⍂', '⍃', '⍄', '⍅', '⍆', '⍇', '⍈',
  '⍉', '⍊', '⍋', '⍌', '⍍', '⍎', '⍏', '⍐',
  '⍑', '⍒', '⍓', '⍔', '⍕', '⍖', '⍗', '⍘',
  '⍚', '⍛', '⍝', '⍞', '⍟', '⍠', '⍡', '⍢',
  '⍣', '⍤', '⍥', '⍦', '⍧', '⍨', '⍩', '⍪',
  '⍫', '⍬', '⍭', '⍮', '⍯', '⍰',
  '⏀', '⏂', '⏄', '⏅', '⏆', '⏇', '⏈', '⏉',
  '⏊', '⏋', '⏌', '⏍', '⏎', '⏏', '⏐', '⏑',
  '⏒', '⏓', '⏔', '⏕', '⏖', '⏗', '⏘', '⏙',
  '⏛', '⏜', '⏝', '⏞', '⏟', '⏠', '⏡',
  '◈', '◉', '◌', '◍', '◎', '◐', '◑', '◒',
  '◓', '◔', '◕', '◖', '◗', '◧', '◨', '◩',
  '◪', '◫', '◬', '◭', '◮', '◯',
  '⊂', '⊃', '⊄', '⊅', '⊆', '⊇', '⊈', '⊉',
  '⊊', '⊋', '⊌', '⊍', '⊎', '⊏', '⊐', '⊒',
  '⊓', '⊔', '⊕', '⊖', '⊗', '⊘', '⊙', '⊚',
  '⊛', '⊜', '⊝', '⊞', '⊟', '⊠', '⊡',
  'ꓤ', 'ꓥ', 'ꓦ', 'ꓧ', 'ꓨ', 'ꓩ', 'ꓪ', 'ꓫ',
  'ꓬ', 'ꓭ', 'ꓮ', 'ꓯ', 'ꓰ', 'ꓱ', 'ꓲ', 'ꓳ',
  'ꓴ', 'ꓵ', 'ꓶ', 'ꓷ', 'ꓸ', 'ꓹ', 'ꓺ', 'ꓻ',
]

const randomGlyph = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)]

// Fisher-Yates shuffle
const shuffle = (arr) => {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

const buildDecodeOrder = (chars, mode) => {
  const nonSpaceIndices = chars
    .map((ch, i) => ch !== ' ' ? i : -1)
    .filter(i => i !== -1)

  if (mode === 'random') return shuffle(nonSpaceIndices)

  if (mode === 'word') {
    const words = []
    let current = []
    chars.forEach((ch, i) => {
      if (ch === ' ') {
        if (current.length) words.push(current)
        current = []
      } else {
        current.push(i)
      }
    })
    if (current.length) words.push(current)
    return words.flat()
  }

  // ltr
  return nonSpaceIndices
}

const useAlienDecodeText = (ref, { text, duration = 10, scrambleWindow = 120, mode = 'ltr', autoPlay = false, onComplete }) => {
  const chars = (text || '').split('')
  const frozenGlyphs = useRef([])
  const states = useRef([])
  const displays = useRef([])
  const decodeOrder = useRef([])
  const rafId = useRef(null)
  const startTime = useRef(0)
  const [, forceUpdate] = useReducer(x => x + 1, 0)
  const isInitialized = useRef(false)

  // init frozen glyphs
  const initIdle = useCallback(() => {
    const newFrozen = chars.map(ch => ch === ' ' ? '\u00A0' : randomGlyph())
    frozenGlyphs.current = newFrozen
    states.current = chars.map(() => 'idle')
    displays.current = [...newFrozen]
    decodeOrder.current = buildDecodeOrder(chars, mode)
  }, [text, mode])

  // init on mount
  if (!isInitialized.current) {
    initIdle()
    isInitialized.current = true
  }

  const play = useCallback(() => {
    if (rafId.current) cancelAnimationFrame(rafId.current)

    // reinit if text changed
    initIdle()
    forceUpdate()

    startTime.current = performance.now()
    const order = decodeOrder.current
    const revealTimes = order.map((_, i) => i * duration)
    const scrambleStarts = revealTimes.map(t => Math.max(0, t - scrambleWindow))
    let allDecoded = false

    const loop = () => {
      if (allDecoded) return
      const elapsed = performance.now() - startTime.current
      let changed = false

      for (let i = 0; i < order.length; i++) {
        const idx = order[i]
        const prev = states.current[idx]

        if (prev === 'decoded') continue

        if (elapsed >= revealTimes[i]) {
          states.current[idx] = 'decoded'
          displays.current[idx] = chars[idx]
          changed = true
        } else if (elapsed >= scrambleStarts[i]) {
          states.current[idx] = 'scrambling'
          displays.current[idx] = randomGlyph()
          changed = true
        }
      }

      if (changed) forceUpdate()

      const done = order.every(idx => states.current[idx] === 'decoded')
      if (done) {
        allDecoded = true
        onComplete?.()
        return
      }

      rafId.current = requestAnimationFrame(loop)
    }

    rafId.current = requestAnimationFrame(loop)
  }, [text, duration, scrambleWindow, mode, onComplete, initIdle])

  const reset = useCallback(() => {
    if (rafId.current) cancelAnimationFrame(rafId.current)
    rafId.current = null
    initIdle()
    forceUpdate()
  }, [initIdle])

  useImperativeHandle(ref, () => ({ play, reset }), [play, reset])

  useEffect(() => {
    if (autoPlay) play()
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current)
    }
  }, [autoPlay, play])

  return { displays: displays.current, states: states.current }
}

export default useAlienDecodeText
